import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const FilterPanel = ({ isOpen, onClose, onApplyFilters, currentFilters }) => {
  const [filters, setFilters] = useState({
    sortBy: currentFilters.sortBy || 'symbol',
    sortOrder: currentFilters.sortOrder || 'asc',
    sector: currentFilters.sector || 'all',
    performance: currentFilters.performance || 'all',
    positionSize: currentFilters.positionSize || 'all',
    priceRange: {
      min: currentFilters.priceRange?.min || '',
      max: currentFilters.priceRange?.max || ''
    }
  });

  const sectors = [
    { value: 'all', label: 'All Sectors' },
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'financials', label: 'Financials' },
    { value: 'consumer-discretionary', label: 'Consumer Discretionary' },
    { value: 'communication-services', label: 'Communication Services' },
    { value: 'industrials', label: 'Industrials' },
    { value: 'consumer-staples', label: 'Consumer Staples' },
    { value: 'energy', label: 'Energy' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'materials', label: 'Materials' }
  ];

  const performanceOptions = [
    { value: 'all', label: 'All Performance' },
    { value: 'gainers', label: 'Gainers Only' },
    { value: 'losers', label: 'Losers Only' },
    { value: 'breakeven', label: 'Break Even (±2%)' }
  ];

  const positionSizeOptions = [
    { value: 'all', label: 'All Positions' },
    { value: 'large', label: 'Large (>₹830,000)' },
    { value: 'medium', label: 'Medium (₹83,000-₹830,000)' },
    { value: 'small', label: 'Small (<₹83,000)' }
  ];

  const sortOptions = [
    { value: 'symbol', label: 'Symbol' },
    { value: 'companyName', label: 'Company Name' },
    { value: 'marketValue', label: 'Market Value' },
    { value: 'gainLoss', label: 'Gain/Loss' },
    { value: 'gainLossPercent', label: 'Gain/Loss %' },
    { value: 'shares', label: 'Shares' },
    { value: 'currentPrice', label: 'Current Price' }
  ];

  const handleFilterChange = (key, value) => {
    if (key === 'priceRange') {
      setFilters(prev => ({
        ...prev,
        priceRange: { ...prev.priceRange, ...value }
      }));
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleApplyFilters = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      sortBy: 'symbol',
      sortOrder: 'asc',
      sector: 'all',
      performance: 'all',
      positionSize: 'all',
      priceRange: { min: '', max: '' }
    };
    setFilters(resetFilters);
    onApplyFilters(resetFilters);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-modal md:hidden" onClick={onClose} />
      
      {/* Filter Panel */}
      <div className={`
        fixed bottom-0 left-0 right-0 bg-card border-t border-border z-modal
        md:relative md:border md:rounded-lg md:shadow-lg
        transform transition-transform duration-300 ease-out
        ${isOpen ? 'translate-y-0' : 'translate-y-full md:translate-y-0'}
        max-h-[80vh] overflow-y-auto
      `}>
        <div className="p-4 border-b border-border md:border-b-0">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Filter & Sort</h3>
            <Button variant="ghost" size="icon" onClick={onClose} className="md:hidden">
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Sort Options */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Sort By</h4>
            <div className="grid grid-cols-1 gap-3">
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              
              <div className="flex space-x-2">
                <Button
                  variant={filters.sortOrder === 'asc' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange('sortOrder', 'asc')}
                  fullWidth
                >
                  <Icon name="ArrowUp" size={16} />
                  <span className="ml-2">Ascending</span>
                </Button>
                <Button
                  variant={filters.sortOrder === 'desc' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleFilterChange('sortOrder', 'desc')}
                  fullWidth
                >
                  <Icon name="ArrowDown" size={16} />
                  <span className="ml-2">Descending</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Sector Filter */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Sector</h4>
            <select
              value={filters.sector}
              onChange={(e) => handleFilterChange('sector', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {sectors.map(sector => (
                <option key={sector.value} value={sector.value}>
                  {sector.label}
                </option>
              ))}
            </select>
          </div>

          {/* Performance Filter */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Performance</h4>
            <select
              value={filters.performance}
              onChange={(e) => handleFilterChange('performance', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {performanceOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Position Size Filter */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Position Size</h4>
            <select
              value={filters.positionSize}
              onChange={(e) => handleFilterChange('positionSize', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {positionSizeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Price Range Filter */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Price Range</h4>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                placeholder="Min price"
                value={filters.priceRange.min}
                onChange={(e) => handleFilterChange('priceRange', { min: e.target.value })}
                min="0"
                step="0.01"
              />
              <Input
                type="number"
                placeholder="Max price"
                value={filters.priceRange.max}
                onChange={(e) => handleFilterChange('priceRange', { max: e.target.value })}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={handleResetFilters} fullWidth>
              <Icon name="RotateCcw" size={16} />
              <span className="ml-2">Reset</span>
            </Button>
            <Button onClick={handleApplyFilters} fullWidth>
              <Icon name="Filter" size={16} />
              <span className="ml-2">Apply Filters</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterPanel;